<template>
  <CardWithTitle
    :item-id="itemId"
    :request-status="requestStatus"
    :options-data="cardOptionsWithCSVExportOption"
    :has-not-classified="hasNotClassified"
    @call-option-menu="callOptionMenu"
    @reload="$emit('reload', $event)"
    @on-show-not-classified="onShowNotClassified"
  >
    <v-row
      no-gutters
      class="pie-chart-container"
      justify="center"
    >
      <HighCharts
        v-if="series.length"
        :ref="chartRef"
        :key="chartRef"
        class="pie-chart ma-0 pa-0"
        :options="chartOptions"
      />
    </v-row>
  </CardWithTitle>
</template>

<script>
import { Chart } from 'highcharts-vue';
import highChartsBasics from '@/mixins/highcharts-basics';
import exportModel from '@/mixins/export-model';
import cardsModel from '@/mixins/cards-model';
import merge from 'lodash/merge';
import CardWithTitle from '@/components/cards/models/CardWithTitle';
import { formatNumber } from '@/utils/formatter';

export default {
  name: 'MultiplePieChart',
  components: {
    HighCharts: Chart,
    CardWithTitle,
  },
  mixins: [
    highChartsBasics,
    exportModel,
    cardsModel,
  ],
  props: {
    requestStatus: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
    series: {
      type: [Array, Object],
      default: () => ([]),
    },
    cardOptions: {
      type: [Array, Object],
      default: () => ([]),
    },
  },
  data() {
    return {
      hideNotClassified: true,
      notClassifiedLabels: [this.$t('MultiplePieChart.pluralNotClassified'), this.$t('MultiplePieChart.singularNotClassified')],
      notShowClassifiedLabel: this.$t('MultiplePieChart.showNotClassified'),
      showClassifiedLabel: this.$t('MultiplePieChart.showClassified'),
      highchartInstance: null,
    };
  },
  computed: {
    chartOptions() {
      const customOptions = {
        chart: {
          type: 'pie',
          plotBackgroundColor: null,
          plotBorderWidth: 0,
          plotShadow: false,
        },
        plotOptions: {
          pie: {
            borderColor: 'black',
            borderWidth: 0.5,
            dataLabels: {
              formatter() {
                if (!this.point || !this.percentage) {
                  return;
                }
                const maxChars = 8;
                const minPercToShow = 1;
                if (this.percentage > minPercToShow) {
                  const pointName = this.point.name.length > maxChars ? `${this.point.name.substring(0, maxChars)}...` : this.point.name;
                  const response = `
                    <span class="pie-chart" title="${pointName}">
                        ${pointName}
                    </span>
                    <span>
                        ${formatNumber(this.point.y)} (${formatNumber(this.point.percentage.toFixed(1))}%)
                    </span>`;
                  return response;
                }
              },
              distance: '-25%',
            },
            showInLegend: false,
          },
        },
        tooltip: {
          followPointer: false,
          formatter() {
            return `
                <b>${this.key}:</b> ${formatNumber(this.y)}
            `;
          },
        },
        series: this.seriesFormatted,
      };
      return merge({}, this.highchartsBasics, this.highchartTheme, customOptions, this.cardOptions);
    },
    seriesFormatted() {
      if (this.hasNotClassified && this.hideNotClassified) {
        return this.series[1].data.filter(series => !this.notClassifiedLabels.includes(series.name));
      }
      return this.series;
    },
    hasNotClassified() {
      return Boolean(this.series[1].data.find(series => this.notClassifiedLabels.includes(series.name)));
    },
    showNotClassifiedLabel() {
      if (this.hideNotClassified) {
        return this.notShowClassifiedLabel;
      }
      return this.showClassifiedLabel;
    },
  },
  methods: {
    onShowNotClassified() {
      this.hideNotClassified = !this.hideNotClassified;
    },
  },
};
</script>

<style scoped lang="scss">
  .pie-chart-container {
    height: 100%;
    width: 100%;
  }

  .pie-chart {
    height: 100%;
    width: 100%;
    padding: 10px;

    &--with-not-classified {
      height: calc(100% - 36px) !important;
    }
  }

  ::v-deep {
    .highcharts-container,
    svg {
      overflow: visible !important;
    }
  }
</style>
